/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import {
  HiOutlineEye,
  HiOutlineRefresh,
  HiOutlineSparkles,
  HiOutlineTrash,
} from "react-icons/hi";
import { FaRegCheckSquare, FaRegSquare } from "react-icons/fa";

import ListPage from "../../layout/ListPage";
import {
  Button,
  Card,
  ConfirmDialog,
  IconButton,
} from "../../ui";
import type { Action, Column } from "../../ui";
import Service from "../../api/service";
import { _PAGE_SIZE_ } from "../../lib/other/tools";
import { _TYPES_ } from "../doseeData/utils/types";
import { useAdminContext } from "../../context/admin/AdminContext";
import { useDoSeeDataContext } from "../../context/DoSeeDataContext";
import {
  pushCountry,
  pushOneEvent,
} from "../../lib/sysNavigation/SysNavigation";
// @ts-ignore — JS helper.
import { getType } from "../contentStats/helper";
// @ts-ignore — existing JSX companion.
import Preview from "./Preview";
// @ts-ignore — existing JSX message component.
import { MyMessage } from "../components/messageUtils";

type EventRow = {
  event_id: number | string;
  title?: string;
  thumbnail?: string;
  createdAt?: string;
  status?: number;
  [key: string]: unknown;
};

const editRoute = "/data/event/edit";

const Overview: React.FC = () => {
  const { deleteOneContent, updateOneContent } = useDoSeeDataContext() as any;
  const { sysAdmin, setSingleAdmin } = useAdminContext() as any;
  const isSuperAdmin = ["super_admin", "admin"].includes(sysAdmin?.role);

  const [events, setEvents] = useState<EventRow[] | null>(null);
  const [count, setCount] = useState<number | null>(null);
  const [prompt, setPrompt] = useState("");
  const [promptId, setPromptId] = useState<number | string | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(_PAGE_SIZE_);
  const [query, setQuery] = useState("");

  const [selectedIds, setSelectedIds] = useState<(number | string)[]>([]);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  const [previewEvent, setPreviewEvent] = useState<EventRow | null>(null);

  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!events) getEvents(page, pageSize);
    if (!promptId) getPrompt();
  }, []);

  const getEvents = async (p = 1, size = _PAGE_SIZE_) => {
    const { data } = await Service.get(
      `/api/v1/admin/generated/event/?page=${p}&pageSize=${size}`,
    );
    if (data) {
      setCount(data.count);
      setEvents(data.data);
    }
  };

  const getPrompt = async () => {
    const { data } = await Service.get(`/api/v1/open_ai/?page=1&pageSize=50`);
    if (data?.data?.[0]) {
      setPrompt(data.data[0].prompt ?? "");
      setPromptId(data.data[0].open_ai_helper_id);
    }
  };

  const savePrompt = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!promptId) return;
    setLoading(true);
    const { status } = await Service.put(`/api/v1/open_ai/update/${promptId}`, {
      cron_day_one: 1,
      cron_day_two: 4,
      prompt,
    });
    const ok = status === 200 || status === 201;
    setLoading(false);
    setError(!ok);
    setSuccess(ok);
    setMessage(ok ? "Prompt updated." : "Failed to update prompt.");
  };

  const deleteMany = async () => {
    setBulkLoading(true);
    for (const id of selectedIds) {
      await deleteOneContent({ data_id: id, content: _TYPES_.EVENT });
    }
    setBulkLoading(false);
    setSelectedIds([]);
    setShowBulkDelete(false);
    await getEvents(page, pageSize);
  };

  const suspendEvent = async (row: EventRow) => {
    setSingleAdmin({
      ids: [sysAdmin?.admin_id],
      type: getType({ field: _TYPES_.EVENT }),
      route: "/generated",
    });
    await updateOneContent({
      data_id: row.event_id,
      content: _TYPES_.EVENT,
      data: {
        country_id: (row as any).country_id,
        description: (row as any).description,
        first_level_id: (row as any).first_level_id,
        fourth_level_id: (row as any).fourth_level_id,
        second_level_id: (row as any).second_level_id,
        slug: row.title?.toLowerCase().replace(/\s/g, "_"),
        status: row.status === 1 ? 0 : 1,
        third_level_id: (row as any).third_level_id,
        thumbnail: row.thumbnail,
        title: row.title,
        url_images: (row as any).url_images,
      },
    });
    await getEvents(page, pageSize);
  };

  const onEdit = (row: EventRow) => {
    pushOneEvent(row);
    pushCountry({
      country: { id: 0, name: "", slug: "", route: "/generated" },
    });
    setSingleAdmin({
      ids: [sysAdmin?.admin_id],
      type: getType({ field: _TYPES_.EVENT }),
      route: "/generated",
    });
  };

  const rows = useMemo<EventRow[] | null>(() => {
    if (!events) return null;
    const q = query.trim().toLowerCase();
    if (!q) return events;
    return events.filter((e) =>
      String(e.title ?? "").toLowerCase().includes(q),
    );
  }, [events, query]);

  const visibleIds = useMemo(
    () => (rows ?? []).map((r) => r.event_id),
    [rows],
  );
  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => visibleIds.includes(id)));
  }, [visibleIds]);

  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const toggleAll = () => {
    setSelectedIds((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...prev, ...visibleIds])),
    );
  };

  const toggleOne = (id: number | string) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const columns: Column<EventRow>[] = [
    {
      header: "Select",
      className: "w-16",
      cell: (row) => (
        <IconButton
          aria-label={selectedIds.includes(row.event_id) ? "Unselect" : "Select"}
          variant="ghost"
          size="sm"
          onClick={() => toggleOne(row.event_id)}
        >
          {selectedIds.includes(row.event_id) ? (
            <FaRegCheckSquare className="h-4 w-4 text-brand" />
          ) : (
            <FaRegSquare className="h-4 w-4 text-gray-400" />
          )}
        </IconButton>
      ),
    },
    {
      header: "Event",
      cell: (row) => (
        <div className="flex items-center gap-3">
          {row.thumbnail ? (
            <img
              src={row.thumbnail}
              alt={row.title ?? ""}
              className="h-12 w-16 rounded-md object-cover ring-1 ring-gray-200"
            />
          ) : (
            <span className="flex h-12 w-16 items-center justify-center rounded-md bg-brand/10 text-brand ring-1 ring-inset ring-brand/15">
              <HiOutlineSparkles className="h-4 w-4" />
            </span>
          )}
          <div className="min-w-0">
            <p className="truncate font-medium text-gray-900">
              {row.title ?? "—"}
            </p>
            {row.createdAt ? (
              <p className="text-[11px] text-gray-500">
                {new Date(row.createdAt).toDateString()}
              </p>
            ) : null}
          </div>
        </div>
      ),
    },
  ];

  const rowActions = (row: EventRow): Action[] => [
    {
      label: "Preview",
      icon: <HiOutlineEye className="h-3.5 w-3.5" />,
      onClick: () => setPreviewEvent(row),
    },
  ];

  const bulkToolbar = (
    <Card padding="sm" className="flex flex-wrap items-center gap-2">
      <Button
        variant="secondary"
        size="sm"
        onClick={toggleAll}
        disabled={visibleIds.length === 0}
      >
        {allVisibleSelected ? (
          <FaRegCheckSquare className="h-3.5 w-3.5 text-brand" />
        ) : (
          <FaRegSquare className="h-3.5 w-3.5" />
        )}
        {allVisibleSelected ? "Unselect visible" : "Select visible"}
      </Button>

      <Button
        variant={selectedIds.length > 0 ? "destructive" : "secondary"}
        size="sm"
        onClick={() => selectedIds.length > 0 && setShowBulkDelete(true)}
        disabled={selectedIds.length === 0 || bulkLoading}
      >
        <HiOutlineTrash className="h-3.5 w-3.5" />
        {selectedIds.length > 0
          ? `Delete ${selectedIds.length} ${selectedIds.length === 1 ? "event" : "events"}`
          : "Delete selected"}
      </Button>

      <Button variant="ghost" size="sm" onClick={() => getEvents(page, pageSize)}>
        <HiOutlineRefresh className="h-3.5 w-3.5" />
        Refresh
      </Button>
    </Card>
  );

  const promptSection = (
    <>
      <MyMessage
        error={error}
        message={message}
        success={success}
        onExpired={() => {
          setError(false);
          setSuccess(false);
        }}
        onClick={() => {
          setError(false);
          setSuccess(false);
          setMessage("");
        }}
      />

      <form onSubmit={savePrompt}>
        <Card padding="md" className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">
                Content Generation Prompt
              </h2>
              <p className="text-xs text-gray-500">
                Tweak the description used to generate events.
              </p>
            </div>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? "Saving…" : "Save changes"}
            </Button>
          </div>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={6}
            className="w-full rounded-xl border border-gray-200 bg-white p-3 text-sm text-gray-900 shadow-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
          />
          <div className="text-right text-xs text-gray-500">
            {prompt.length.toLocaleString()} characters
          </div>
        </Card>
      </form>
    </>
  );

  return (
    <>
      <ListPage<EventRow>
        title="Generated Events"
        subtitle="Content produced by the generation pipeline."
        icon={<HiOutlineSparkles className="h-5 w-5" />}
        topSlot={promptSection}
        toolbar={bulkToolbar}
        rows={rows}
        columns={columns}
        rowKey={(r) => r.event_id}
        rowActions={rowActions}
        search={{
          value: query,
          onChange: setQuery,
          placeholder: "Search by title…",
        }}
        pagination={{
          page,
          pageSize,
          total: count,
          onChange: ({ page: p, pageSize: ps }) => {
            setPage(p);
            setPageSize(ps);
            getEvents(p, ps);
          },
        }}
        empty="No generated events yet."
      />

      <ConfirmDialog
        open={showBulkDelete}
        destructive
        title={`Delete ${selectedIds.length} ${selectedIds.length === 1 ? "event" : "events"}?`}
        description="This cannot be undone."
        confirmLabel={bulkLoading ? "Deleting…" : "Delete"}
        onCancel={() => setShowBulkDelete(false)}
        onConfirm={deleteMany}
      />

      {previewEvent ? (
        <Preview
          event={previewEvent}
          isSuperAdmin={isSuperAdmin}
          suspension={() => suspendEvent(previewEvent)}
          editRoute={editRoute}
          onEdit={() => onEdit(previewEvent)}
          setShowDeleteModal={async () => {
            await deleteOneContent({
              data_id: previewEvent.event_id,
              content: _TYPES_.EVENT,
            });
            setPreviewEvent(null);
            await getEvents(page, pageSize);
          }}
          setShowPreview={(v: boolean) => !v && setPreviewEvent(null)}
        />
      ) : null}
    </>
  );
};

export default Overview;
